import {
  EntityStatusControlConfig,
  StatusConfig,
  ArchiveReasonConfig,
} from "../types";
import type { KanbanColumn } from "../../../types/pipeline.types";

/**
 * Candidate-specific status configuration
 * ADO Feature #4936: Candidate Status Workflow
 */

export const CANDIDATE_STATUS_CONFIG: Record<string, StatusConfig> = {
  preparation: { label: "Preparation", color: "#8b5cf6", bgColor: "#f3e8ff" },
  tech_check: { label: "Tech-check", color: "#3b82f6", bgColor: "#dbeafe" },
  ready_for_sale: {
    label: "Ready for Sale",
    color: "#10b981",
    bgColor: "#d1fae5",
  },
  to_come_back: { label: "To come back", color: "#f59e0b", bgColor: "#fef3c7" },
  hired: { label: "Hired", color: "#14b8a6", bgColor: "#ccfbf1" },
  archived: { label: "Archived", color: "#6b7280", bgColor: "#f3f4f6" },
};

export const CANDIDATE_ARCHIVE_REASONS: Record<string, ArchiveReasonConfig> = {
  failed_tech_check: {
    label: "Failed Tech Check",
    description: "Did not pass technical assessment",
  },
  gdpr_request: {
    label: "GDPR Request",
    description: "Candidate requested data deletion",
  },
  resignation: {
    label: "Resignation",
    description: "Candidate no longer interested",
  },
  duplicate: { label: "Duplicate", description: "Merged with another profile" },
  other: { label: "Other", description: "Other reason (specify in notes)" },
};

export const CANDIDATE_STATUS_ORDER = [
  "preparation",
  "tech_check",
  "ready_for_sale",
  "to_come_back",
  "hired",
  "archived",
];

export const CANDIDATE_VALID_TRANSITIONS: Record<string, string[]> = {
  preparation: ["tech_check", "archived"],
  tech_check: ["ready_for_sale", "preparation", "archived"],
  ready_for_sale: ["hired", "to_come_back", "tech_check", "archived"],
  to_come_back: ["ready_for_sale", "archived"],
  hired: ["ready_for_sale"],
  archived: [], // Terminal state
};

/**
 * Get reason why a candidate transition is not allowed
 */
export const getCandidateDisabledReason = (
  currentStatus: string,
  targetStatus: string,
): string | null => {
  if (currentStatus === targetStatus) {
    return "Current status";
  }

  if (currentStatus === "hired") {
    if (targetStatus === "ready_for_sale") return null; // Allow return to pipeline
    return "Hired candidates can only return to Ready for Sale";
  }

  if (currentStatus === "archived") {
    return "Archived is a terminal status";
  }

  if (currentStatus === "preparation") {
    if (targetStatus === "ready_for_sale")
      return "Must complete Tech-check first";
    if (targetStatus === "hired") return "Must complete workflow first";
    if (targetStatus === "to_come_back")
      return "Must reach Ready for Sale first";
  }

  if (currentStatus === "tech_check") {
    if (targetStatus === "hired") return "Must be Ready for Sale first";
    if (targetStatus === "to_come_back") return "Must be Ready for Sale first";
  }

  if (currentStatus === "ready_for_sale") {
    if (targetStatus === "preparation") return "Cannot revert to Preparation";
  }

  if (currentStatus === "to_come_back") {
    if (targetStatus === "preparation") return "Cannot revert to Preparation";
    if (targetStatus === "tech_check") return "Cannot revert to Tech-check";
    if (targetStatus === "hired") return "Must be Ready for Sale first";
  }

  return "Not allowed from current status";
};

export const CANDIDATE_KANBAN_COLUMNS: KanbanColumn[] = [
  { key: "preparation", label: "Preparation", statuses: ["preparation"] },
  { key: "tech_check", label: "Tech-check", statuses: ["tech_check"] },
  {
    key: "ready_for_sale",
    label: "Ready for Sale",
    statuses: ["ready_for_sale"],
  },
  { key: "to_come_back", label: "To Come Back", statuses: ["to_come_back"] },
  { key: "hired", label: "Hired", statuses: ["hired"] },
  { key: "archived", label: "Archived", statuses: ["archived"] },
];

export const CANDIDATE_BOARD_HIDDEN_BY_DEFAULT = ["hired", "archived"];

export const CANDIDATE_CONFIG: EntityStatusControlConfig = {
  statusConfig: CANDIDATE_STATUS_CONFIG,
  archiveReasons: CANDIDATE_ARCHIVE_REASONS,
  entityName: "Candidate",
  entityNamePlural: "Candidates",
  defaultStatus: "preparation",
  queryKeys: {
    entity: "candidate",
    entities: "candidates",
    transitions: "candidateTransitions",
  },
};
